import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject, Subscription, from, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { ValueService } from '../../../shared/value.service';
import { Utils } from '../../../shared/utils';
import { consts } from '../../../consts';
import { Option } from '../../../shared/models/option.model';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { LoadingDialogService } from '../../../shared/ui/loading/loading-dialog.service';
import { ModelTaxService } from '../model-tax.service';
import { Model184Service } from '../model-184.service';
import { SINO_AEAT } from '../tax-catalogs';
import { TAX_FORM_DECLARATION_STATUS, TaxFormDeclarationModel } from '../tax-form-declaration.model';
import { TaxFormHistoryDialogComponent, TaxFormHistoryResult } from '../tax-form-history/tax-form-history-dialog.component';
import { TaxFieldOriginDialogComponent } from '../tax-field-origin/tax-field-origin-dialog.component';
import {
  ACTIVIDAD_PRINCIPAL_184,
  EXTRANJERO_OBJETO_184,
  EXTRANJERO_TIPO_184,
  Model184,
  Model184Validation,
  TIPO_DECLARACION_184,
  TIPO_ENTIDAD_184,
  normalizarModelo184,
  nuevoModelo184
} from './taxes-184.model';
import { Model184RentaDialogComponent } from './model-184-renta-dialog/model-184-renta-dialog.component';
import { Model184MiembroDialogComponent } from './model-184-miembro-dialog/model-184-miembro-dialog.component';
import { Model184OrigenSolicitud } from './model-184-rentas/model-184-rentas.component';

const PERIODO_ANUAL = '0A';

@Component({
  selector: 'convfact-model184',
  templateUrl: './model-184.component.html',
  styleUrls: ['./model-184.component.css'],
  standalone: false
})
export class Model184Component implements OnInit, OnDestroy {
  KEY_PATH = 'PATH_URL_MODEL_184';

  path: string;
  isLoading = true;
  isSaving = false;
  mostrarGuia = false;

  data: Model184 = nuevoModelo184(new Date().getFullYear() - 1);
  validations: Model184Validation[] = [];
  declaracionActual: TaxFormDeclarationModel;

  ejercicioSel: string;
  readonly ejercicioOptions: Option[] = Utils.GetListYears()
    .map((anio: number) => ({ label: anio.toString(), value: anio.toString() }));

  readonly tipoDeclaracionOptions: Option[] = TIPO_DECLARACION_184;
  readonly tipoEntidadOptions: Option[] = TIPO_ENTIDAD_184;
  readonly actividadOptions: Option[] = ACTIVIDAD_PRINCIPAL_184;
  readonly extranjeroTipoOptions: Option[] = EXTRANJERO_TIPO_184;
  readonly extranjeroObjetoOptions: Option[] = EXTRANJERO_OBJETO_184;
  readonly sinoOptions: Option[] = SINO_AEAT;

  readonly secciones: { clave: string, titulo: string }[] = [
    { clave: 'declarante', titulo: 'Declarante' },
    { clave: 'declaracion', titulo: 'Declaración' },
    { clave: 'entidad', titulo: 'Entidad' },
    { clave: 'rentas', titulo: 'Rentas' },
    { clave: 'miembros', titulo: 'Miembros' },
    { clave: 'atribuciones', titulo: 'Atribuciones' },
    { clave: 'presentacion', titulo: 'Presentación' }
  ];

  private readonly campos: { clave: string, etiqueta: string, region: string }[] = [
    { clave: 'sujetoNIF', etiqueta: 'NIF de la entidad', region: 'declarante' },
    { clave: 'sujetoDenominacion', etiqueta: 'Denominación o razón social de la entidad', region: 'declarante' },
    { clave: 'ejercicio', etiqueta: 'Ejercicio', region: 'declarante' },
    { clave: 'telefono', etiqueta: 'Teléfono', region: 'declarante' },
    { clave: 'contacto', etiqueta: 'Persona con quien relacionarse', region: 'declarante' },
    { clave: 'sustitutiva', etiqueta: 'Tipo de declaración normal complementaria sustitutiva', region: 'declaracion' },
    { clave: 'justificanteAnterior', etiqueta: 'Justificante de la declaración anterior', region: 'declaracion' },
    { clave: 'justificante', etiqueta: 'Justificante de esta declaración', region: 'declaracion' },
    { clave: 'tipoEntidad', etiqueta: 'Tipo de entidad', region: 'entidad' },
    { clave: 'actividadPrincipal', etiqueta: 'Actividad principal', region: 'entidad' },
    { clave: 'extranjeroTipo', etiqueta: 'Entidad constituida en el extranjero', region: 'entidad' },
    { clave: 'extranjeroObjeto', etiqueta: 'Objeto de la entidad extranjera', region: 'entidad' },
    { clave: 'extranjeroPais', etiqueta: 'Clave de país extranjero', region: 'entidad' },
    { clave: 'porcentajeResidentes', etiqueta: 'Porcentaje de renta atribuible a miembros residentes', region: 'entidad' },
    { clave: 'tributaIS', etiqueta: 'Miembros tributan en el Impuesto sobre Sociedades', region: 'entidad' },
    { clave: 'cifraNegocios', etiqueta: 'Importe neto de la cifra de negocios', region: 'entidad' },
    { clave: 'representanteNIF', etiqueta: 'NIF del representante', region: 'entidad' },
    { clave: 'representanteNombre', etiqueta: 'Nombre del representante', region: 'entidad' },
    { clave: 'rentas', etiqueta: 'Rentas de la entidad', region: 'rentas' },
    { clave: 'miembros', etiqueta: 'Miembros socios partícipes', region: 'miembros' }
  ];

  private readonly KEY_REGIONES = 'cf-184-regiones';
  private readonly KEY_CONFIRMADAS = 'cf-184-confirmadas';

  regiones: { [region: string]: boolean } = {};
  confirmadas: { [region: string]: boolean } = {};
  seccionActiva = 'declarante';
  buscadorCampo: string;

  private readonly recalculo$ = new Subject<void>();
  private readonly subscripcion = new Subscription();
  private version = 0;
  private justificanteConsultado = '';

  constructor(
    private service: Model184Service,
    private draftService: ModelTaxService<any>,
    private valueService: ValueService,
    private dialog: MatDialog,
    private confirmService: ConfirmService,
    private notification: NotificationService,
    private loading: LoadingDialogService
  ) { }

  ngOnInit(): void {
    this.getPath();
    this.cargarRegiones();

    this.ejercicioSel = (new Date().getFullYear() - 1).toString();

    this.subscripcion.add(this.recalculo$.pipe(
      debounceTime(300),
      switchMap(() => {
        const enviada = this.version;

        return from(this.service.recalculate(this.payload())).pipe(
          map(respuesta => ({ respuesta, enviada })),
          catchError(() => {
            this.notification.warning('No se han podido recalcular los totales');
            return of(null);
          })
        );
      })
    ).subscribe(resultado => {
      if (resultado && resultado.enviada === this.version) {
        this.aplicarRecalculo(resultado.respuesta);
      }
    }));

    this.cargarEjercicio(true);
  }

  ngOnDestroy(): void {
    this.subscripcion.unsubscribe();
  }

  get ejercicio(): number {
    return Number(this.ejercicioSel) || this.data.ejercicio;
  }

  get tipoSel(): string {
    if (this.data.sustitutiva) return 'S';

    return this.data.complementaria ? 'C' : 'N';
  }

  get esAnterior(): boolean {
    return this.data.complementaria || this.data.sustitutiva;
  }

  get tributaSN(): string {
    return this.data.tributaIS ? 'S' : 'N';
  }

  get errores(): Model184Validation[] {
    return this.validations.filter(item => item.type !== 'WARNING');
  }

  get avisosValidacion(): Model184Validation[] {
    return this.validations.filter(item => item.type === 'WARNING');
  }

  get estadoDeclaracion(): string {
    if (!this.declaracionActual) return 'Sin guardar';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented) return 'Presentada';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.downloaded) return 'Fichero descargado';

    return 'Borrador guardado';
  }

  get varianteDeclaracion(): string {
    if (!this.declaracionActual) return 'neutral';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented) return 'success';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.downloaded) return 'info';

    return 'warning';
  }

  get nombreFichero(): string {
    const nif = (this.data.sujetoNIF || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();

    return [nif, this.ejercicioSel].filter(parte => !!parte).join('_') + '.184';
  }

  trackByIndex(i: number) { return i; }

  mensaje(clave: string): string {
    const item = this.validations.find(v => v.key === clave);

    return item ? item.message : '';
  }

  filaDe(item: Model184Validation, coleccion: string): number {
    const coincidencia = new RegExp(`^${coleccion}\\[(\\d+)\\]\\.`).exec(item.key || '');

    return coincidencia ? Number(coincidencia[1]) : -1;
  }

  etiquetaSalto(item: Model184Validation): string {
    if (this.filaDe(item, 'rentas') >= 0) return 'Ver renta';
    if (this.filaDe(item, 'miembros') >= 0) return 'Ver miembro';

    return 'Ir al campo';
  }

  private numeroOCero(valor: any): number {
    const convertido = Number(String(valor ?? '').replace(',', '.'));

    return isNaN(convertido) ? 0 : convertido;
  }

  private payload(): Model184 {
    return {
      ...this.data,
      modelo: '184',
      ejercicio: this.ejercicio,
      porcentajeResidentes: this.numeroOCero(this.data.porcentajeResidentes),
      cifraNegocios: this.numeroOCero(this.data.cifraNegocios)
    };
  }

  private programarRecalculo() {
    this.version++;
    this.recalculo$.next();
  }

  private aplicarRecalculo(respuesta: Model184) {
    const calculado = normalizarModelo184(respuesta);

    this.data = {
      ...this.data,
      rentas: calculado.rentas,
      miembros: calculado.miembros,
      atribuciones: calculado.atribuciones,
      numeroMiembros: calculado.numeroMiembros,
      numeroRegistrosEntidad: calculado.numeroRegistrosEntidad,
      avisos: calculado.avisos,
      justificante: calculado.justificante,
      justificanteAnterior: calculado.justificanteAnterior
    };
  }

  onTipo(valor: string) {
    this.data.complementaria = valor === 'C';
    this.data.sustitutiva = valor === 'S';

    if (valor === 'N') {
      this.data.justificanteAnterior = '';
      this.justificanteConsultado = '';
    }

    this.programarRecalculo();
    this.traerJustificante();
  }

  onJustificanteAnterior(valor: string) {
    this.data.justificanteAnterior = valor;

    if ((valor || '').trim().length === 13) {
      this.traerJustificante();
    }
  }

  onTributa(valor: string) {
    this.data.tributaIS = valor === 'S';
  }

  onExtranjeroTipo(valor: string) {
    this.data.extranjeroTipo = valor || '';

    if (!this.data.extranjeroTipo) {
      this.data.extranjeroObjeto = '';
      this.data.extranjeroPais = '';
    }
  }

  private traerJustificante() {
    const justificante = (this.data.justificanteAnterior || '').trim();

    if (!this.esAnterior || justificante.length !== 13 || justificante === this.justificanteConsultado) return;

    this.justificanteConsultado = justificante;

    this.loading.run('Buscando el justificante...',
      this.service.calculate(this.ejercicioSel, justificante, this.data.sustitutiva ? 'S' : 'C'))
      .then(modelo => {
        this.data.avisos = modelo.avisos;

        if (modelo.avisos.length) {
          this.notification.warning(modelo.avisos[0]);
        }
      })
      .catch(() => {
        this.justificanteConsultado = '';
        this.notification.error('No se ha podido comprobar el justificante');
      });
  }

  cambiarEjercicio() {
    this.cargarEjercicio(false);
  }

  private cargarEjercicio(primeraCarga: boolean) {
    const ejercicio = this.ejercicioSel;

    if (!ejercicio) return;

    this.isLoading = primeraCarga;

    const tarea = (async () => {
      const borradores = await this.draftService.getDrafts('184', ejercicio).catch(() => []);
      const existente = (borradores || [])
        .find(item => item.period === PERIODO_ANUAL && item.taxYear.toString() === ejercicio);

      if (existente) {
        const borrador = await this.draftService.getDraft('184', existente.uid) as Model184;

        if (this.esBorradorVigente(borrador)) {
          return borrador;
        }
      }

      return await this.service.calculate(ejercicio);
    })();

    const ejecucion = primeraCarga ? tarea : this.loading.run('Cargando el ejercicio...', tarea);

    ejecucion.then(modelo => {
      this.isLoading = false;
      this.aplicarModelo(modelo);
      this.loadDeclaracionActual();
    }).catch(() => {
      this.isLoading = false;
      this.notification.error('Ha ocurrido un error inesperado al obtener la información');
    });
  }

  private esBorradorVigente(borrador: Model184): boolean {
    return !!borrador && Array.isArray(borrador.rentas) && Array.isArray(borrador.miembros)
      && borrador.sujetoNIF !== undefined;
  }

  private aplicarModelo(modelo: Model184) {
    this.version++;
    this.data = normalizarModelo184(modelo);
    this.data.ejercicio = Number(this.ejercicioSel) || this.data.ejercicio;
    this.data.periodo = PERIODO_ANUAL;
    this.justificanteConsultado = this.esAnterior ? (this.data.justificanteAnterior || '').trim() : '';
    this.validations = [];

    if (this.data.avisos.length) {
      this.notification.warning(this.data.avisos[0]);
    }
  }

  private loadDeclaracionActual() {
    const ejercicio = this.ejercicioSel;

    this.draftService.getDrafts('184', ejercicio)
      .then(response => {
        this.declaracionActual = (response || [])
          .find(item => item.period === PERIODO_ANUAL && item.taxYear.toString() === ejercicio);
      })
      .catch(() => { });
  }

  calcular() {
    const justificante = this.esAnterior ? (this.data.justificanteAnterior || '').trim() : '';
    const tipo = this.data.sustitutiva ? 'S' : 'C';
    const previo = this.data;

    this.loading.run('Calculando el modelo...', this.service.calculate(this.ejercicioSel, justificante, tipo))
      .then(modelo => {
        this.aplicarModelo(modelo);

        ['telefono', 'contacto', 'representanteNIF', 'representanteNombre', 'tipoEntidad', 'actividadPrincipal']
          .forEach(campo => {
            if (!(this.data as any)[campo] && (previo as any)[campo]) {
              (this.data as any)[campo] = (previo as any)[campo];
            }
          });
      })
      .catch(() => this.notification.error('Ha ocurrido un error inesperado al calcular el modelo'));
  }

  private validacionesDe(coleccion: string, indice: number): Model184Validation[] {
    const prefijo = `${coleccion}[${indice}].`;

    return indice < 0 ? [] : this.validations
      .filter(item => (item.key || '').startsWith(prefijo))
      .map(item => ({ ...item, key: item.key.substring(prefijo.length) }));
  }

  nuevaRenta() {
    this.abrirRenta(-1);
  }

  editarRenta(indice: number) {
    this.abrirRenta(indice);
  }

  private abrirRenta(indice: number) {
    Model184RentaDialogComponent.open(this.dialog, {
      renta: indice < 0 ? null : this.data.rentas[indice],
      ejercicio: this.ejercicio,
      validaciones: this.validacionesDe('rentas', indice)
    }).subscribe(resultado => {
      if (!resultado) return;

      if (indice < 0) {
        this.data = { ...this.data, rentas: [...this.data.rentas, resultado.renta] };
      } else {
        this.data = {
          ...this.data,
          rentas: this.data.rentas.map((item, i) => i === indice ? resultado.renta : item)
        };
      }

      this.validations = [];
      this.notification.ok('Renta guardada');
      this.programarRecalculo();
    });
  }

  eliminarRenta(indice: number) {
    const renta = this.data.rentas[indice];

    if (!renta) return;

    const persistida = renta.origen === 'MANUAL' && !!renta.uid;

    this.confirmService.open({
      title: 'Eliminar renta',
      subtitle: `Clave ${renta.clave}${renta.subclave ? ' / ' + renta.subclave : ''}`,
      message: '¿Seguro que quieres quitar esta renta del modelo 184?',
      tone: 'danger',
      details: [
        { label: 'Clave', value: renta.clave || '-' },
        { label: 'Subclave', value: renta.subclave || '-' },
        { label: 'País', value: renta.pais || '-' }
      ],
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando renta...',
      errorText: 'No se pudo eliminar la renta',
      action: () => persistida ? this.service.deleteRent(renta.uid) : Promise.resolve()
    }).subscribe(eliminada => {
      if (!eliminada) return;

      this.data = { ...this.data, rentas: this.data.rentas.filter((_, i) => i !== indice) };
      this.validations = [];
      this.notification.ok('Renta eliminada');
      this.programarRecalculo();
    });
  }

  verOrigen(solicitud: Model184OrigenSolicitud) {
    const renta = this.data.rentas[solicitud.indice];

    if (!renta) return;

    this.loading.run('Buscando de dónde sale...', this.service.getOrigen(this.ejercicio, renta, solicitud.campo))
      .then(origen => {
        if (!origen) {
          this.notification.warning('Este importe no tiene desglose');
          return;
        }

        this.dialog.open(TaxFieldOriginDialogComponent, {
          width: '720px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          autoFocus: false,
          data: { origen }
        });
      })
      .catch(() => this.notification.warning('Este importe no tiene desglose'));
  }

  nuevoMiembro() {
    this.abrirMiembro(-1);
  }

  editarMiembro(indice: number) {
    this.abrirMiembro(indice);
  }

  private abrirMiembro(indice: number) {
    Model184MiembroDialogComponent.open(this.dialog, {
      miembro: indice < 0 ? null : this.data.miembros[indice],
      ejercicio: this.ejercicio,
      validaciones: this.validacionesDe('miembros', indice)
    }).subscribe(resultado => {
      if (!resultado) return;

      if (indice < 0) {
        this.data = { ...this.data, miembros: [...this.data.miembros, resultado.miembro] };
      } else {
        this.data = {
          ...this.data,
          miembros: this.data.miembros.map((item, i) => i === indice ? resultado.miembro : item)
        };
      }

      this.validations = [];
      this.notification.ok('Miembro guardado');
      this.programarRecalculo();
    });
  }

  eliminarMiembro(indice: number) {
    const miembro = this.data.miembros[indice];

    if (!miembro) return;

    const persistido = miembro.origen === 'MANUAL' && !!miembro.uid;

    this.confirmService.open({
      title: 'Eliminar miembro',
      subtitle: miembro.nombre || miembro.nif || undefined,
      message: '¿Seguro que quieres quitar este miembro del modelo 184?',
      tone: 'danger',
      details: [
        { label: 'NIF', value: miembro.nif || '-' },
        { label: 'Participación', value: miembro.participacion + ' %' }
      ],
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando miembro...',
      errorText: 'No se pudo eliminar el miembro',
      action: () => persistido ? this.service.deleteMember(miembro.uid) : Promise.resolve()
    }).subscribe(eliminado => {
      if (!eliminado) return;

      this.data = { ...this.data, miembros: this.data.miembros.filter((_, i) => i !== indice) };
      this.validations = [];
      this.notification.ok('Miembro eliminado');
      this.programarRecalculo();
    });
  }

  validate() {
    this.loading.run('Validando la declaración...', this.service.validate(this.payload()))
      .then(lista => {
        this.validations = lista;
        const errores = this.errores;

        if (!lista.length) {
          this.notification.ok('La declaración no tiene errores');
          return;
        }

        if (!errores.length) {
          this.notification.warning('Se puede presentar, pero hay avisos que revisar');
          return;
        }

        this.notification.warning(errores.length === 1 ? 'Hay 1 error que corregir' : `Hay ${errores.length} errores que corregir`);
        this.irAlCampo(errores[0]);
      })
      .catch(() => this.notification.error('Ha ocurrido un error inesperado al validar'));
  }

  irAlCampo(item: Model184Validation) {
    const renta = this.filaDe(item, 'rentas');

    if (renta >= 0) {
      this.abrirRegion('rentas');
      this.abrirRenta(renta);
      return;
    }

    const miembro = this.filaDe(item, 'miembros');

    if (miembro >= 0) {
      this.abrirRegion('miembros');
      this.abrirMiembro(miembro);
      return;
    }

    if (item.key) {
      this.scrollToElement(item.key);
    }
  }

  getFile() {
    if (!Utils.IsTierFour()) {
      this.notification.warning(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.loading.run('Generando el fichero...', this.service.getFile(this.payload()))
      .then(resultado => {
        this.validations = [];
        FileSaver.saveAs(resultado.blob, resultado.nombre || this.nombreFichero);
        this.mostrarGuia = true;
        this.notification.ok('Fichero descargado');
        this.loadDeclaracionActual();
      })
      .catch(async err => {
        const cuerpo = await this.leerError(err);

        if (cuerpo) {
          this.mostrarFaltantes(cuerpo);
          return;
        }

        this.notification.error('Ha ocurrido un error inesperado al generar el fichero');
      });
  }

  private async leerError(err: any): Promise<any> {
    try {
      if (err?.error instanceof Blob) {
        return JSON.parse(await err.error.text());
      }

      return err?.error ?? null;
    } catch {
      return null;
    }
  }

  private mostrarFaltantes(respuesta: any) {
    this.validations = Array.isArray(respuesta?.value) ? respuesta.value : [];
    const faltan = this.errores.length;

    if (!faltan) {
      this.notification.error(respuesta?.error?.messages?.[0] || 'No se ha podido generar el fichero');
      return;
    }

    this.notification.warning(faltan === 1
      ? 'Hay 1 error que impide generar el fichero'
      : `Hay ${faltan} errores que impiden generar el fichero`);

    this.irAlCampo(this.errores[0]);
  }

  saveDraft() {
    this.isSaving = true;

    this.loading.run('Guardando el borrador...', this.draftService.saveDraft(
      { ...this.payload(), periodo: PERIODO_ANUAL },
      {
        taxYear: this.ejercicio,
        amount: 0,
        declarationType: this.data.sustitutiva ? 'S' : this.data.complementaria ? 'C' : null,
        receiptNumber: this.esAnterior ? this.data.justificanteAnterior : ''
      }))
      .then(() => {
        this.isSaving = false;
        this.notification.ok('Borrador guardado');
        this.loadDeclaracionActual();
      })
      .catch(() => {
        this.isSaving = false;
        this.notification.error('No se ha podido guardar el borrador');
      });
  }

  openHistory() {
    this.dialog.open(TaxFormHistoryDialogComponent, {
      width: '960px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { modelo: '184', titulo: 'modelo 184' }
    }).afterClosed().subscribe((result: TaxFormHistoryResult) => {
      if (result?.action === 'open') {
        this.openDeclaration(result.declaration);
      }
    });
  }

  private openDeclaration(declaration: TaxFormDeclarationModel) {
    this.loading.run('Abriendo la declaración...', this.draftService.getDraft('184', declaration.uid))
      .then(async (modelo: Model184) => {
        this.ejercicioSel = declaration.taxYear.toString();

        if (this.esBorradorVigente(modelo)) {
          this.aplicarModelo(modelo);
        } else {
          this.notification.warning('Esta declaración se guardó con el formato anterior: se muestran los datos calculados');
          this.aplicarModelo(await this.service.calculate(this.ejercicioSel));
        }

        this.loadDeclaracionActual();
      })
      .catch(() => this.notification.error('No se ha podido abrir la declaración'));
  }

  importFile(event: any) {
    const file: File = event?.target?.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      this.loading.run('Importando el fichero...', this.service.importFile(reader.result as string))
        .then(modelo => {
          if (modelo.ejercicio && this.ejercicioOptions.some(item => item.value === modelo.ejercicio.toString())) {
            this.ejercicioSel = modelo.ejercicio.toString();
          }

          this.aplicarModelo(modelo);
          this.loadDeclaracionActual();
          this.notification.ok('Fichero importado');
        })
        .catch(err => this.notification.error(err?.error?.error?.messages?.[0] || 'El fichero no se ha podido importar'));
    };

    reader.onerror = () => this.notification.error('No se ha podido leer el fichero');
    reader.readAsText(file, 'ISO-8859-1');
    event.target.value = '';
  }

  marcarPresentada() {
    if (!this.declaracionActual) {
      this.notification.warning('Guarda el borrador antes de marcarlo como presentado');
      return;
    }

    this.confirmService.open({
      title: 'Marcar como presentada',
      subtitle: `Ejercicio ${this.ejercicioSel}`,
      message: 'Se guardará como presentada en el historial. Esto no presenta la declaración: la presentación se hace en la sede de la Agencia Tributaria.',
      icon: 'check_circle',
      tone: 'info',
      confirmText: 'Marcar como presentada',
      action: () => this.draftService.setDraftStatus('184', this.declaracionActual.uid,
        TAX_FORM_DECLARATION_STATUS.presented)
    }).subscribe(confirmed => {
      if (confirmed) {
        this.notification.ok('Declaración marcada como presentada');
        this.loadDeclaracionActual();
      }
    });
  }

  descartarBorrador() {
    if (!this.declaracionActual) return;

    const presentada = this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented;

    this.confirmService.open({
      title: 'Descartar borrador',
      subtitle: `Ejercicio ${this.ejercicioSel}`,
      message: presentada
        ? 'Esta declaración está marcada como presentada. Si la descartas, se borra del historial y el formulario vuelve a los datos calculados.'
        : 'Se borra lo guardado de este ejercicio y el formulario vuelve a los datos calculados a partir de tus datos.',
      icon: 'restore',
      tone: 'danger',
      confirmText: 'Descartar y reiniciar',
      action: () => this.draftService.deleteDraft('184', this.declaracionActual.uid)
    }).subscribe(confirmed => {
      if (!confirmed) return;

      this.declaracionActual = null;
      this.validations = [];
      this.mostrarGuia = false;
      this.recorridoInicial();
      this.notification.ok('Borrador descartado');
      this.cargarEjercicio(false);
    });
  }

  colapsada(region: string): boolean {
    return !!this.regiones[region];
  }

  confirmada(region: string): boolean {
    return !!this.confirmadas[region];
  }

  toggleRegion(region: string) {
    this.regiones[region] = !this.regiones[region];

    if (!this.regiones[region]) {
      this.seccionActiva = region;
    }

    this.guardarRegiones();
  }

  get todoCompactado(): boolean {
    return this.secciones.every(seccion => this.regiones[seccion.clave]);
  }

  alternarTodo() {
    const compactar = !this.todoCompactado;

    this.secciones.forEach(seccion => this.regiones[seccion.clave] = compactar);
    this.guardarRegiones();
  }

  private abrirRegion(region: string) {
    this.seccionActiva = region;

    if (this.regiones[region]) {
      this.regiones[region] = false;
      this.guardarRegiones();
    }
  }

  irALaSeccion(region: string) {
    this.abrirRegion(region);
    this.bajarALaSeccion(region);
  }

  confirmarRegion(region: string) {
    this.confirmadas[region] = true;
    this.regiones[region] = true;

    const posicion = this.secciones.findIndex(item => item.clave === region);
    const siguiente = this.secciones
      .slice(posicion + 1)
      .find(candidata => !this.confirmadas[candidata.clave]);

    if (siguiente) {
      this.regiones[siguiente.clave] = false;
    }

    const destino = siguiente ? siguiente.clave : region;

    this.seccionActiva = destino;
    this.guardarRegiones();
    this.bajarALaSeccion(destino);
  }

  private bajarALaSeccion(region: string) {
    setTimeout(() => {
      const element = document.getElementById(`region-${region}`);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  private normalizar(texto: string): string {
    return (texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  irAlCampoBuscado() {
    const buscado = this.normalizar(this.buscadorCampo);

    if (!buscado) return;

    const campo = this.campos.find(item => this.normalizar(item.clave) === buscado)
      || this.campos.find(item => this.normalizar(`${item.etiqueta} ${item.clave}`).includes(buscado));

    if (!campo) {
      this.notification.warning(`No hay ningún campo "${this.buscadorCampo}" en el modelo 184`);
      return;
    }

    this.scrollToElement(campo.clave);
  }

  scrollToElement(key: string) {
    if (!key) return;

    const campo = this.campos.find(item => item.clave === key);

    if (campo) {
      this.abrirRegion(campo.region);
    }

    setTimeout(() => {
      const element = document.getElementById(key);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  private cargarRegiones() {
    try {
      const regiones = localStorage.getItem(this.KEY_REGIONES);
      const confirmadas = localStorage.getItem(this.KEY_CONFIRMADAS);

      this.confirmadas = confirmadas ? JSON.parse(confirmadas) : {};

      if (regiones) {
        this.regiones = JSON.parse(regiones);
        return;
      }
    } catch {
      this.confirmadas = {};
    }

    this.recorridoInicial();
  }

  private recorridoInicial() {
    this.regiones = {};
    this.confirmadas = {};

    this.secciones.forEach((seccion, indice) => this.regiones[seccion.clave] = indice > 0);
    this.seccionActiva = this.secciones[0].clave;

    this.guardarRegiones();
  }

  private guardarRegiones() {
    try {
      localStorage.setItem(this.KEY_REGIONES, JSON.stringify(this.regiones));
      localStorage.setItem(this.KEY_CONFIRMADAS, JSON.stringify(this.confirmadas));
    } catch { }
  }

  redirectFileTax() {
    window.open(this.path, '_blank');
  }

  getPath() {
    this.valueService.getValue(this.KEY_PATH)
      .then(response => {
        this.path = response?.data?.data;
      })
      .catch(() => { });
  }
}
